// User guide — how to use the app (Fundamentals section; more topics later)
import React from "react";
import { Link } from "react-router-dom";

const BRAND = {
  bg: "#0a0a0f",
  panel: "#1a1a24",
  border: "#2a2a3a",
  text: "#e8e8f0",
  textSecondary: "#b8b8c8",
  muted: "#888898",
  accent: "#6366f1",
};

const DEMO_TICKER = "AAPL";

const FIGURE_STYLE = {
  marginTop: 16,
  marginBottom: 8,
  borderRadius: 12,
  overflow: "hidden",
  border: `1px solid ${BRAND.border}`,
  background: "#0d0d12",
  lineHeight: 0,
};

function Section({ id, title, children }) {
  return (
    <section id={id} style={{ marginBottom: 44 }}>
      <h2 style={{ margin: "0 0 12px", fontSize: "1.35rem", fontWeight: 700, color: BRAND.text }}>{title}</h2>
      {children}
    </section>
  );
}

function Figure({ src, alt, caption, maxWidthPx }) {
  const frameStyle = {
    ...FIGURE_STYLE,
    ...(maxWidthPx
      ? {
          maxWidth: maxWidthPx,
          marginLeft: "auto",
          marginRight: "auto",
        }
      : {}),
  };
  return (
    <figure style={{ margin: "16px 0 0" }}>
      <div style={frameStyle}>
        <img src={src} alt={alt} style={{ width: "100%", maxWidth: "100%", height: "auto", display: "block", verticalAlign: "top" }} />
      </div>
      {caption && (
        <figcaption style={{ marginTop: 10, fontSize: 13, color: BRAND.muted, lineHeight: 1.5 }}>{caption}</figcaption>
      )}
    </figure>
  );
}

export default function TutorialPage({ isAuthenticated = false }) {
  const [isMobile, setIsMobile] = React.useState(typeof window !== "undefined" && window.innerWidth <= 768);
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const p = {
    margin: "0 0 14px",
    color: BRAND.textSecondary,
    fontSize: isMobile ? 15 : 16,
    lineHeight: 1.65,
  };

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: isMobile ? "8px 0 24px" : "12px 0 32px" }}>
      <h1 style={{ margin: "0 0 12px", fontSize: isMobile ? 26 : 32, fontWeight: 800, color: BRAND.text, letterSpacing: "-0.02em" }}>
        User guide
      </h1>
      <p style={{ ...p, marginBottom: 20 }}>
        This page walks you through the <strong style={{ color: BRAND.text }}>Fundamentals</strong> view step by step. A
        &quot;fundamental&quot; is a measure of how the business is doing—revenue, earnings per share (EPS), margins, and
        similar metrics—shown next to the stock price so you can compare company performance with how the market prices the
        stock. Instructions for the Indicators page will be added here later.
      </p>
      {isAuthenticated ? (
        <p style={{ ...p, marginBottom: 28 }}>
          After you{" "}
          <Link to={`/stock/${DEMO_TICKER}/fundamentals`} style={{ color: BRAND.accent, textDecoration: "none", fontWeight: 600 }}>
            open Fundamentals for a ticker
          </Link>
          , use the sections below in order from top to bottom of the page.
        </p>
      ) : (
        <p style={{ ...p, marginBottom: 28 }}>
          To try these steps in the app,{" "}
          <Link to="/auth" style={{ color: BRAND.accent, textDecoration: "none", fontWeight: 600 }}>
            sign in
          </Link>
          , search for a stock, then open the <strong style={{ color: BRAND.text }}>Fundamentals</strong> tab. You can also
          keep reading below—the screenshots show what each part of the screen looks like.
        </p>
      )}

      <Section id="choose-metric" title="1. Choose what to plot: Fundamental Metrics">
        <p style={p}>
          Tap <strong style={{ color: BRAND.text }}>Choose metric</strong> (or the equivalent control on your screen). A
          dialog titled <strong style={{ color: BRAND.text }}>Fundamental Metrics</strong> lists every metric you can
          overlay. Pick <strong style={{ color: BRAND.text }}>one</strong> at a time with the radio buttons—reported figures
          like Revenue or EPS, or <strong style={{ color: BRAND.text }}>forecast</strong> lines such as Revenue (Forecast)
          or EPS (Forecast). Choose <strong style={{ color: BRAND.text }}>None</strong> if you only want the price. Press{" "}
          <strong style={{ color: BRAND.text }}>Apply</strong> to update the chart.
        </p>
        <Figure
          src="/tutorial/choose-metrics.png"
          alt="Fundamental Metrics dialog with radio options and Apply"
          caption="Select a single fundamental (or a forecast option), then Apply."
          maxWidthPx={480}
        />
      </Section>

      <Section id="chart" title="2. Read the chart: fundamental vs. price">
        <p style={p}>
          The main chart uses <strong style={{ color: BRAND.text }}>two vertical axes</strong> so scales stay readable: the{" "}
          <strong style={{ color: BRAND.text }}>left axis</strong> shows the fundamental you chose (often on a log scale for
          wide-ranging values), and the <strong style={{ color: BRAND.text }}>right axis</strong> shows{" "}
          <strong style={{ color: BRAND.text }}>price</strong>. Price is usually a solid line; the fundamental may appear as
          a dashed or dotted line. Use the zoom presets (for example 6m, 1Y, 5Y) to focus on the period you care about.
        </p>
        <p style={p}>
          <strong style={{ color: BRAND.text }}>Why this helps:</strong> When both lines trend together, the market has
          often rewarded improvements in that metric; when they diverge, it can be a signal to dig deeper (valuation,
          sentiment, or other drivers).
        </p>
        <Figure
          src="/tutorial/fundamental-vs-price.png"
          alt="Dual-axis chart with fundamental on the left and price on the right"
          caption="Left axis: selected fundamental. Right axis: stock price. Compare shape and timing, not just level."
        />
      </Section>

      <Section id="correlations" title="3. Correlation with price (numbers below the chart)">
        <p style={p}>
          Under the chart, the <strong style={{ color: BRAND.text }}>Correlation with price</strong> table scores how
          closely each fundamental has moved with the stock price. Values range from about <strong style={{ color: BRAND.text }}>-1</strong>{" "}
          to <strong style={{ color: BRAND.text }}>+1</strong>. A value near <strong style={{ color: BRAND.text }}>+1</strong>{" "}
          means the metric and price tended to rise and fall together; near <strong style={{ color: BRAND.text }}>0</strong>{" "}
          means little linear relationship; negative values mean they often moved in opposite directions.
        </p>
        <p style={p}>
          Columns such as <strong style={{ color: BRAND.text }}>All</strong>, <strong style={{ color: BRAND.text }}>10y</strong>,{" "}
          <strong style={{ color: BRAND.text }}>5y</strong>, <strong style={{ color: BRAND.text }}>3y</strong>, and{" "}
          <strong style={{ color: BRAND.text }}>1y</strong> show whether that relationship held over the long run or has
          changed recently—useful when a stock&apos;s story has shifted.
        </p>
        <Figure
          src="/tutorial/correlations-table.png"
          alt="Table of fundamentals vs price correlation across time windows"
          caption="Sort or scan to see which fundamentals have tracked price most closely in each period."
        />
      </Section>

      <Section id="next-4q" title="4. Next four quarters: analyst consensus">
        <p style={p}>
          The <strong style={{ color: BRAND.text }}>Next 8 quarters (analyst consensus)</strong> table shows what analysts
          expect for upcoming quarters—typically <strong style={{ color: BRAND.text }}>EPS</strong> and{" "}
          <strong style={{ color: BRAND.text }}>Revenue</strong>—with one column per quarter-end date. The{" "}
          <strong style={{ color: BRAND.text }}>Forecast accuracy (5y)</strong> column summarizes how often past analyst
          estimates were close to actual results over the last five years (higher is better for trusting the numbers).
        </p>
        <p style={p}>
          The footer on that screen explains the difference between <strong style={{ color: BRAND.text }}>forecast accuracy</strong>{" "}
          (were the estimates right?) and <strong style={{ color: BRAND.text }}>correlation</strong> (did the metric move with
          price?). Both matter, but they answer different questions.
        </p>
        <Figure
          src="/tutorial/next-4q-forecast.png"
          alt="Next four quarters analyst consensus table with EPS and Revenue"
          caption="Consensus expectations by quarter, plus historical forecast accuracy where available."
        />
      </Section>

      <Section id="forecast-chart" title="5. Visualize EPS or revenue forecasts on the chart">
        <p style={p}>
          Return to <strong style={{ color: BRAND.text }}>Choose metric</strong> and select a{" "}
          <strong style={{ color: BRAND.text }}>forecast</strong> option—for example Revenue (Forecast) or EPS (Forecast).
          The chart will plot analyst expectations through future quarters (often as dotted or dashed lines) alongside
          price. That makes it easy to see whether the market has historically followed the expected path of earnings or
          sales, and how far forward the forecast extends.
        </p>
        <Figure
          src="/tutorial/next-4q-visualize.png"
          alt="Chart with price and forecast fundamental series over time"
          caption="Pick a forecast from the metric list to see projected fundamentals vs. price on the same timeline."
        />
      </Section>

      <Section id="price-target" title="6. Price forecast (next four quarters)">
        <p style={p}>
          The <strong style={{ color: BRAND.text }}>Price forecast (next 8 quarters)</strong> section turns the relationships
          above into <strong style={{ color: BRAND.text }}>implied price levels</strong> for upcoming quarters. The app fits a{" "}
          <strong style={{ color: BRAND.text }}>linear regression</strong> of historical price against one driver at a time
          (for example EPS or Revenue). It then plugs in <strong style={{ color: BRAND.text }}>analyst consensus</strong> for
          the next four quarters to produce a point estimate. The <strong style={{ color: BRAND.text }}>95% confidence interval</strong>{" "}
          shows a range of uncertainty around each point—not a guarantee of future prices.
        </p>
        <p style={p}>
          <strong style={{ color: BRAND.text }}>How to interpret it responsibly:</strong> The short explanation under the
          title reminds you that the model is more reliable when <strong style={{ color: BRAND.text }}>analyst forecast
          accuracy</strong> is high and when <strong style={{ color: BRAND.text }}>EPS and Revenue correlate strongly</strong>{" "}
          with price in the correlation table. Treat the output as a structured scenario, not advice.
        </p>
        <Figure
          src="/tutorial/price-target-forecast.png"
          alt="Price forecast table with EPS and Revenue models and confidence intervals"
          caption="Two model rows (e.g. EPS-based and Revenue-based) with quarterly point estimates and 95% CI ranges."
        />
      </Section>

      <p style={{ ...p, marginTop: 8, padding: "14px 16px", background: BRAND.panel, borderRadius: 12, border: `1px solid ${BRAND.border}` }}>
        <strong style={{ color: BRAND.text }}>Coming soon:</strong> a walkthrough of the Indicators page will be added to
        this guide.
      </p>
    </div>
  );
}
